import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCreateTeamMember } from '@/api/settings/useCreateTeamMember';
import { useGetAccountDetails } from '@/api/settings/useGetAccountDetails';
import { useUpdateTeamMember } from '@/api/workspaces/useUpdateTeamMember';
import { useAppSelector } from '@/app/hooks';
import { Button } from '@/component-library/primitives/Button/Button';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';
import { CreateTeamMemberDetails } from '@/models/TeamMembers';
import { selectCurrentWorkspace, selectWorkspaces } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import { UserRoles } from '@/modules/shared/enums/enums';
import { AppRoutes } from '@/utils/routes/router';

import RoleCard from './RoleCard';

const CreateTeamMember = () => {
  const createTeamMemberMutation = useCreateTeamMember();
  const updateTeamMemberMutation = useUpdateTeamMember();
  const location = useLocation() as any;
  const editEmail = location?.state?.editEmail as string | undefined;
  const editGivenName = location?.state?.givenName as string | undefined;
  const editFamilyName = location?.state?.familyName as string | undefined;
  const editUserRole = location?.state?.userRole as string | undefined;
  const workspaces = useAppSelector(selectWorkspaces);
  const currentWorkspace = useAppSelector(selectCurrentWorkspace);
  const { data: accountDetails } = useGetAccountDetails();
  const filteredWorkspaces = workspaces?.map((workspace) => {
    return {
      name: workspace.name,
      role: UserRoles.TENANT_ADMIN,
      access: true, // Grant access by default since there's typically only one workspace
    };
  });
  const [workspacesData, setWorkspacesData] = useState(filteredWorkspaces);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const { t } = useTranslation<any>();
  const translate = t as unknown as (key: string) => string;
  const [accessErrorKey, setAccessErrorKey] = useState('');

  const schema = useMemo(() => {
    return yup
      .object()
      .shape({
        firstName: yup.string().trim().required('CommonErrors.firstNameRequired'),
        lastName: yup.string().trim().required('CommonErrors.lastNameRequired'),
        email: yup.string().trim().email('CommonErrors.emailFormatValidation').required('CommonErrors.emailRequired'),
      })
      .required();
  }, []);

  // Keep local workspacesData in sync with Redux workspaces
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      setWorkspacesData(
        workspaces.map((workspace) => ({
          name: workspace.name,
          role: UserRoles.TENANT_ADMIN,
          access: true,
        })),
      );
    }
  }, [workspaces]);

  useEffect(() => {
    let timerBeforeNavigation: NodeJS.Timeout;
    if (toastInfo.toastType === 'success') {
      timerBeforeNavigation = setTimeout(() => navigate(AppRoutes.teamMembers), 2000);
    }
    return () => {
      clearTimeout(timerBeforeNavigation);
    };
  }, [navigate, toastInfo]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateTeamMemberDetails>({
    resolver: yupResolver(schema),
    defaultValues: editEmail ? { email: editEmail } : undefined,
  });

  // Prefill from navigation state when editing
  useEffect(() => {
    if (editEmail) {
      if (editGivenName) setValue('firstName', editGivenName);
      if (editFamilyName) setValue('lastName', editFamilyName);
      if (editUserRole) setUserAssignRole(editUserRole);
    }
  }, [editEmail, editGivenName, editFamilyName, editUserRole, setValue]);

  const onSubmit = (data: CreateTeamMemberDetails) => {
    // Send only the default/current workspace; keep support for multiple on BE
    const selectedWs = currentWorkspace
      ? [
          {
            id: currentWorkspace.workspaceId || currentWorkspace.id,
            name: currentWorkspace.name,
            role: userAssignRole,
            access: true,
          },
        ]
      : workspacesData?.length
      ? [{ ...workspacesData[0], id: undefined }]
      : [];
    const atLeastOneHasAccess =
      selectedWs.length === 0 ? true : selectedWs.some((workspace) => workspace.access === true);
    if (!atLeastOneHasAccess) {
      setAccessErrorKey('CreateTeamMemberPage.accessToAtLeastOneWorkspaceError');
    } else {
      // If in edit mode, update first/last name and role of existing user
      if (editEmail) {
        updateTeamMemberMutation.mutate(
          { username: editEmail, givenName: data.firstName, familyName: data.lastName, userRole: userAssignRole },
          {
            onSuccess: () => {
              setAccessErrorKey('');
              setToastInfo({ message: translate('CreateTeamMemberPage.toastEditSuccess'), toastType: 'success' });
              setOpen(true);
            },
            onError: (err: any) => {
              setAccessErrorKey('');
              const serverMsg = err?.response?.data;
              const message =
                typeof serverMsg === 'string' && serverMsg ? serverMsg : translate('CreateTeamMemberPage.toastError');
              setToastInfo({ message, toastType: 'error' });
              setOpen(true);
            },
          },
        );
        return;
      }

      const sendData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        role: userAssignRole,
        userPoolId: accountDetails?.userPoolId as string,
        workspaces: selectedWs,
      };
      createTeamMemberMutation.mutate(sendData as CreateTeamMemberDetails, {
        onSuccess: () => {
          setAccessErrorKey('');
          setToastInfo({ message: translate('CreateTeamMemberPage.toastSuccess'), toastType: 'success' });
          setOpen(true);
        },
        onError: (err: any) => {
          setAccessErrorKey('');
          const status = err?.response?.status;
          const serverMsg = err?.response?.data;

          // Extract error message from different response formats
          let errorMessage = '';
          if (status === 409) {
            // User already exists - use server message if available, otherwise use translation
            errorMessage =
              typeof serverMsg === 'string' && serverMsg
                ? serverMsg
                : translate('CreateTeamMemberPage.userExistsError');
          } else if (status === 400) {
            // Bad request - use server message
            errorMessage =
              typeof serverMsg === 'string' && serverMsg
                ? serverMsg
                : 'Invalid data provided. Please check your input.';
          } else if (typeof serverMsg === 'string' && serverMsg) {
            // Use server message if available
            errorMessage = serverMsg;
          } else if (err?.message) {
            // Use error message from error object
            errorMessage = err.message;
          } else {
            // Fallback to generic error
            errorMessage = translate('CreateTeamMemberPage.toastError');
          }

          setToastInfo({ message: errorMessage, toastType: 'error' });
          setOpen(true);
        },
      });
    }
  };

  const updateAccess = (workspaceId: number) => {
    setWorkspacesData((state) =>
      state.map((el, id) => {
        return id === workspaceId ? { ...el, access: !el.access } : el;
      }),
    );
  };

  const disableAll = () => {
    setWorkspacesData((state) =>
      state.map((el) => {
        return { ...el, access: false };
      }),
    );
  };

  const [userAssignRole, setUserAssignRole] = useState<string>(UserRoles.TENANT_ADMIN);
  const handleRoleSelect = (role: string) => {
    setUserAssignRole(() => role);
  };

  const isRoleSelected = (role: string) => userAssignRole === role;

  const pageTitleKey = editEmail ? 'CreateTeamMemberPage.editTitle' : 'CreateTeamMemberPage.title';
  const actionButtonKey = editEmail
    ? 'CreateTeamMemberPage.buttonSaveChanges'
    : 'CreateTeamMemberPage.buttonAddTeamMember';

  return (
    <div className="flex flex-col items-center max-w-[40rem] mx-auto">
      <h1 className="text-custom-black w-full font-medium text-xl mb-8">{translate(pageTitleKey)}</h1>

      <form className="w-full space-y-8" onSubmit={handleSubmit(onSubmit)}>
        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {translate('CreateTeamMemberPage.attributesLabel')}
          </h2>

          <Input
            label={translate('CreateTeamMemberPage.firstNameLabel')}
            placeholder={translate('CreateTeamMemberPage.firstNamePlaceholder')}
            value={watch('firstName') || ''}
            onChange={(e: any) => setValue('firstName', e.target.value, { shouldDirty: true })}
            error={errors.firstName?.message ? translate(errors.firstName.message as string) : ''}
          />

          <Input
            label={translate('CreateTeamMemberPage.lastNameLabel')}
            placeholder={translate('CreateTeamMemberPage.lastNamePlaceholder')}
            value={watch('lastName') || ''}
            onChange={(e: any) => setValue('lastName', e.target.value, { shouldDirty: true })}
            error={errors.lastName?.message ? translate(errors.lastName.message as string) : ''}
          />

          <Input
            label={translate('CreateTeamMemberPage.emailLabel')}
            placeholder={translate('CreateTeamMemberPage.emailPlaceholder')}
            {...register('email')}
            error={errors.email?.message ? translate(errors.email.message as string) : ''}
            disabled={Boolean(editEmail)}
          />
        </section>

        <hr className="w-full border-custom-lightSilver" />

        <section className="space-y-4">
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {translate('CreateTeamMemberPage.roleLabel')}
          </h2>

          <div className="space-y-4">
            <RoleCard
              roleLabel={UserRoles.TENANT_ADMIN}
              description={translate('CreateTeamMemberPage.administratorRoleDescription')}
              isRoleSelected={isRoleSelected}
              handleRoleSelect={handleRoleSelect}
            />
            <RoleCard
              roleLabel={UserRoles.TENANT_MANAGER}
              description={translate('CreateTeamMemberPage.managerRoleDescription')}
              isRoleSelected={isRoleSelected}
              handleRoleSelect={handleRoleSelect}
            />
            <RoleCard
              roleLabel={UserRoles.TENANT_AUTHOR}
              description={translate('CreateTeamMemberPage.authorRoleDescription')}
              isRoleSelected={isRoleSelected}
              handleRoleSelect={handleRoleSelect}
            />
            <RoleCard
              roleLabel={'TenantUser'}
              description={translate('CreateTeamMemberPage.userRoleDescription')}
              isRoleSelected={isRoleSelected}
              handleRoleSelect={handleRoleSelect}
            />
          </div>
        </section>

        <hr className="w-full border-custom-lightSilver" />

        {/* <section>
          <h2 className="text-xs font-semibold text-custom-blue uppercase">
            {t('CreateTeamMemberPage.workspacesLabel')}
          </h2>

          <table className="w-full">
            <thead>
              <tr className="text-xs text-primary-300 font-semibold tracking-[0.24px] border-b-2 border-b-primary-200">
                <th className="text-left pb-2 uppercase">{t('CreateTeamMemberPage.workspaceNameLabel')}</th>
                <th className="text-left pb-2 uppercase">{t('CreateTeamMemberPage.roleLabel')}</th>
                <th className="text-left pb-2 uppercase">{t('CreateTeamMemberPage.accessLabel')}</th>
                <th className="pb-2 text-right">
                  <button
                    type="button"
                    onClick={disableAll}
                    className="uppercase hover:bg-deleteButtonHoverBG text-red-400 rounded-lg px-2 py-3"
                  >
                    <div className="flex flex-row items-center gap-x-1">
                      <StopIcon width="18" height="18" />
                      {t('CreateTeamMemberPage.linkDisableAll')}
                    </div>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {workspacesData?.map((workspace: any, index: number) => (
                <tr key={index} className="text-gray-300 text-sm font-normal">
                  <td className="py-2 w-[40%]">{workspace.name}</td>
                  <td className="py-2 w-[30%]">{userAssignRole}</td>
                  <td className="py-2">
                    <div className="mt-2">
                      <ToggleSwitch value={workspace.access} onChange={() => updateAccess(index)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {accessError && (
            <p className="text-errorRed-500 mt-2 text-xs font-medium flex flex-row items-center gap-2">
              <CircleWarningIcon />
              {accessError}
            </p>
          )}
        </section> */}

        {accessErrorKey && <p className="text-errorRed-500 -mt-4 text-xs font-medium">{translate(accessErrorKey)}</p>}

        <div className="flex items-center justify-end gap-4">
          <Button
            variant="transparent"
            onClick={() => navigate(AppRoutes.teamMembers)}
            disabled={createTeamMemberMutation.isLoading}
          >
            {translate('CreateTeamMemberPage.linkCancel')}
          </Button>

          <Button
            type="submit"
            isLoading={createTeamMemberMutation.isLoading || createTeamMemberMutation.isSuccess}
            disabled={createTeamMemberMutation.isLoading}
          >
            {translate(actionButtonKey)}
          </Button>
        </div>
      </form>

      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};
export default CreateTeamMember;
